import type { ElementType, HTMLAttributes, ReactNode } from "react";

import { cn } from "@src/shared/lib/utils";

export type TextAppearance =
  | "default"
  | "system"
  | "danger"
  | "subdued"
  | "muted";

export type TextSizing = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export interface TextProps extends HTMLAttributes<HTMLElement> {
  tag?: ElementType;
  as?: ElementType;
  appearance?: TextAppearance;
  sizing?: TextSizing;
  fontWeight?: 400 | 500 | 600 | 700;
  children?: ReactNode;
}

const sizingClass: Record<TextSizing, string> = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
};

const appearanceClass: Record<TextAppearance, string> = {
  default: "text-foreground",
  system: "text-muted-foreground",
  subdued: "text-muted-foreground",
  muted: "text-muted-foreground",
  danger: "text-destructive",
};

const weightClass: Record<NonNullable<TextProps["fontWeight"]>, string> = {
  400: "font-normal",
  500: "font-medium",
  600: "font-semibold",
  700: "font-bold",
};

/**
 * Replacement for the legacy vital-based Text/Text2 component. Renders a
 * semantic element with Tailwind typography utilities mapped from the legacy
 * appearance/sizing/fontWeight props so existing call-sites keep working.
 */
export const Text = ({
  tag,
  as,
  appearance = "default",
  sizing = "md",
  fontWeight,
  className,
  children,
  ...rest
}: TextProps) => {
  const Component = (tag ?? as ?? "span") as ElementType;

  return (
    <Component
      className={cn(
        sizingClass[sizing],
        appearanceClass[appearance],
        fontWeight && weightClass[fontWeight],
        "leading-relaxed",
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};
